import React from 'react';
import { Helmet } from 'react-helmet-async';
import AfsfhSeal from './AfsfhSeal';
import { MapPin, Mail, Phone, MessageCircle, Instagram, Heart, Linkedin, Facebook, Music2 } from 'lucide-react';
import { whatsappHref } from '../utils/gtag';
import { openCookieSettings } from './CookieConsent';

const CNHC_URL = '/images/CNHC.png';
const BRAINZ_URL = '/images/brainz-badge.png';

const Footer: React.FC = () => {
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Sharon Clare Hypnotherapy",
            "description": "Professional clinical hypnotherapy services across Northern Ireland. CNHC registered hypnotherapist specializing in anxiety, radiotherapy support, chronic pain, and medical procedure anxiety.",
            "url": "https://www.sharonclare-hypnotherapy.com/",
            "logo": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "telephone": "07932139245",
            "foundingDate": "2020",
            "sameAs": [
              "https://www.instagram.com/sharonclarehypnotherapy/",
              "https://www.facebook.com/SharonClareHypnotherapy",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy",
              "https://www.tiktok.com/@sharonclarehypnot"
            ],
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Wellness Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": "54.5807",
                  "longitude": "-5.9186"
                }
              },
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": "54.5162",
                  "longitude": "-6.0581"
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <footer className="bg-gray-900 text-white section-padding">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand & Description */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="text-white font-bold text-xl">
                Sharon Clare
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Helping clients across Northern Ireland achieve lasting mental and emotional 
              well-being through professional, compassionate clinical hypnotherapy.
            </p>
            <div className="flex items-center space-x-2 text-accent-400">
              <Heart className="w-5 h-5" />
              <span className="text-sm">Transforming lives with care and expertise</span>
            </div>
              <div className="mt-6">
              <h4 className="font-semibold mb-3 text-sm text-gray-200">Professional Membership</h4>
              <AfsfhSeal />
              <div className="mt-4 flex items-center gap-4">
                <img 
                  src={encodeURI(CNHC_URL)} 
                  alt="CNHC Registered" 
                  loading="lazy" 
                  className="h-10 w-auto object-contain bg-white/0"
                />
                <img 
                  src={encodeURI(BRAINZ_URL)} 
                  alt="Brainz Magazine Contributor" 
                  loading="lazy" 
                  className="h-10 w-auto object-contain bg-white/0"
                />
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-6">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-300 font-semibold mb-2">Clinics:</p>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-300">Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ</p>
                      <p className="text-gray-400">Days and times vary — early evening appointments available</p>
                    </div>
                    <div>
                      <p className="text-gray-300 font-semibold mt-2">Lisburn:</p>
                      <p className="text-gray-300">The Arzen Collective, 13B Antrim Street, Lisburn BT28</p>
                    </div>
                    <div>
                      <a
                        href="/book"
                        className="text-accent-400 hover:text-white transition-colors underline"
                      >
                        Book an appointment
                      </a>
                    </div>
                    <div>
                      <p className="text-gray-300">Online sessions also available</p>
                      <p className="text-gray-400">Across Northern Ireland & UK</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-accent-400" />
                <a
                  href="mailto:Sharon@sharonclare-hypnotherapy.com"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Sharon@sharonclare-hypnotherapy.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-accent-400" />
                <a
                  href="tel:07932139245"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  07932 139245
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <MessageCircle className="w-5 h-5 text-accent-400" />
                <a
                  href={whatsappHref("Hi Sharon, I'd like to ask about hypnotherapy.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Message on WhatsApp
                </a>
              </div>
            </div>
          </div>
          
          {/* Services & Social */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-6">Services & Locations</h3>
            <div className="space-y-2 mb-6">
              <a href="/anxiety-stress" className="block text-gray-300 hover:text-white transition-colors">
                Anxiety & Stress Relief
              </a>
              <a href="/radiotherapy-anxiety" className="block text-gray-300 hover:text-white transition-colors">
                Radiotherapy Anxiety
              </a>
              <a href="/persistent-pain" className="block text-gray-300 hover:text-white transition-colors">
                Chronic Pain Management
              </a>
              <a href="/medical-procedure-anxiety" className="block text-gray-300 hover:text-white transition-colors">
                Medical Anxiety
              </a>
              <a href="/ibs-hypnotherapy" className="block text-gray-300 hover:text-white transition-colors">
                IBS Hypnotherapy
              </a>
              <a href="/bladder-urgency-hypnotherapy" className="block text-gray-300 hover:text-white transition-colors">
                Bladder Urgency
              </a>
            </div>

            <h4 className="text-sm font-semibold mb-3 text-gray-300">Our Locations</h4>
            <div className="space-y-2 mb-8">
              <a href="/belfast-hypnotherapy" className="block text-gray-300 hover:text-white transition-colors text-sm">
                Hypnotherapy Belfast
              </a>
              <a href="/lisburn-hypnotherapy" className="block text-gray-300 hover:text-white transition-colors text-sm">
                Hypnotherapy Lisburn
              </a>
              <a href="/book" className="block text-gray-300 hover:text-white transition-colors text-sm">
                Book an Appointment
              </a>
              <a href="/how-change-happens" className="block text-gray-300 hover:text-white transition-colors text-sm">
                How Change Happens
              </a>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Follow Sharon</h4>
              <div className="space-y-3">
                <a 
                  href="https://instagram.com/sharonclarehypnotherapy" 
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <Instagram className="w-5 h-5 text-accent-400 group-hover:text-accent-300" />
                  <span>@sharonclarehypnotherapy</span>
                </a>
                <a
                  href="https://www.linkedin.com/in/sharonclare-hypnotherapy"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <Linkedin className="w-5 h-5 text-accent-400 group-hover:text-accent-300" />
                  <span>/in/sharonclare-hypnotherapy</span>
                </a>
                <a
                  href="https://www.facebook.com/SharonClareHypnotherapy/"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <Facebook className="w-5 h-5 text-accent-400 group-hover:text-accent-300" />
                  <span>/SharonClareHypnotherapy</span>
                </a>
                <a
                  href="https://www.tiktok.com/@sharonclarehypnot"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <Music2 className="w-5 h-5 text-accent-400 group-hover:text-accent-300" />
                  <span>@sharonclarehypnot</span>
                </a>
              </div>
            </div>

            <div className="mt-8">
              <h4 className="font-semibold mb-4">Clinics & Professional Profiles</h4>
              <div className="space-y-2">
                <span className="block text-gray-300">Belfast Wellness Clinic, 228 Ormeau Road, Belfast</span>
                <span className="block text-gray-300">The Arzen Collective, 13B Antrim Street, Lisburn BT28</span>
                <a href="https://www.hypnotherapy-directory.org.uk/hypnotherapists/sharon-clare" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-white transition-colors">Hypnotherapy Directory Profile</a>
                <a href="https://www.brainzmagazine.com/executive-contributor/sharon-clare" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-white transition-colors">Brainz Magazine Articles</a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              &copy; 2025 Sharon Clare Hypnotherapy. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <a href="/privacy-policy" className="hover:text-white transition-colors">
                Privacy &amp; GDPR
              </a>
              <span>•</span>
              <a href="/terms" className="hover:text-white transition-colors">
                Payment &amp; Cancellation Terms
              </a>
              <span>•</span>
              <a href="/cookie-policy" className="hover:text-white transition-colors">
                Cookie Policy
              </a>
              <span>•</span>
              <button type="button" onClick={openCookieSettings} className="hover:text-white transition-colors">
                Cookie settings
              </button>
              <span>•</span>
              <span>Northern Ireland</span>
            </div>
          </div>
          <p className="text-gray-600 text-xs mt-4 text-center">Website by <a href="https://www.roseyco.com/uk/services/web-development" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Rosey Co</a></p>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;